import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ThreadCategory } from '../lib/api';
import { useUiStore } from '../lib/store';
import styles from './SplitTabs.module.css';

/**
 * Superhuman's Split Inbox, and the top of the main column — this row is where a
 * search bar would be in webmail, which is exactly why the app does not read as
 * webmail once the row is tabs instead.
 *
 * The treatment is the measured one: horizontal TEXT tabs, left-aligned, in the
 * form "Name count". Active is near-black in light and near-white in dark;
 * inactive is mid-grey. No underline, no pill, no box — weight and colour carry
 * the whole state, and the counts disappear at zero because emptiness is shown
 * by absence.
 */

/** null is the unfiltered split — Superhuman's "Important" plus everything else. */
type Split = { key: string; label: string; category: ThreadCategory | null };

const SPLITS: readonly Split[] = [
  { key: 'all', label: 'All', category: null },
  { key: 'priority', label: 'Important', category: 'priority' },
  { key: 'people', label: 'People', category: 'people' },
  { key: 'newsletters', label: 'Newsletters', category: 'newsletters' },
  { key: 'notifications', label: 'Notifications', category: 'notifications' },
  { key: 'receipts', label: 'Receipts', category: 'receipts' },
];

/** Splits are an inbox idea; they are meaningless over Sent or Trash. */
const SPLIT_VIEWS = new Set(['inbox', 'all', 'unread']);

/** Matches the drawer's scan so the two counts agree and the query is shared. */
const UNREAD_SCAN_LIMIT = 200;

/** What the row says when there are no splits to show — the view's own name. */
const VIEW_LABEL: Record<string, string> = {
  inbox: 'Inbox',
  all: 'All mail',
  unread: 'Unread',
  starred: 'Starred',
  snoozed: 'Snoozed',
  sent: 'Sent',
  archive: 'Archive',
  trash: 'Trash',
};

/**
 * Superhuman binds Tab / Shift+Tab to the splits, but Superhuman is a desktop
 * app that owns the whole keyboard. In a browser, stealing Tab while focus is
 * on <body> would leave a keyboard-only user unable to reach anything at all —
 * a hard keyboard trap. So Tab is only borrowed when focus is already parked
 * inside the list pane on a non-focusable element, and `[` / `]` are the
 * bindings that always work. Both are advertised in the tooltip.
 */
function tabIsFree(): boolean {
  const active = document.activeElement as HTMLElement | null;
  if (!active || active === document.body) return false;
  if (!active.closest('[data-pane="list"]')) return false;
  if (active.isContentEditable) return false;
  if (/^(INPUT|TEXTAREA|SELECT|BUTTON|A)$/.test(active.tagName)) return false;
  return active.tabIndex === -1;
}

export function SplitTabs() {
  const view = useUiStore((s) => s.view);
  const category = useUiStore((s) => s.category);
  const setCategory = useUiStore((s) => s.setCategory);
  const accountFilter = useUiStore((s) => s.accountFilter);
  const paletteOpen = useUiStore((s) => s.paletteOpen);
  const settingsOpen = useUiStore((s) => s.settingsOpen);
  const appearanceOpen = useUiStore((s) => s.appearanceOpen);
  const composerOpen = useUiStore((s) => s.composer.open);
  const showSplitTabs = useUiStore((s) => s.appearance.showSplitTabs);

  const stripRef = useRef<HTMLDivElement>(null);

  const enabled = showSplitTabs && SPLIT_VIEWS.has(view);

  // The drawer already runs exactly this query, so the counts cost nothing.
  const unreadQuery = useQuery({
    queryKey: ['threads', { view: 'unread', limit: UNREAD_SCAN_LIMIT }],
    queryFn: () => api.threads({ view: 'unread', limit: UNREAD_SCAN_LIMIT }),
    enabled,
  });

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const thread of unreadQuery.data?.threads ?? []) {
      if (accountFilter !== null && !thread.accountIds.includes(accountFilter)) continue;
      map.set('all', (map.get('all') ?? 0) + 1);
      if (thread.category) map.set(thread.category, (map.get(thread.category) ?? 0) + 1);
    }
    return map;
  }, [unreadQuery.data, accountFilter]);

  const activeIndex = Math.max(
    0,
    SPLITS.findIndex((s) => s.category === category),
  );

  const move = useCallback(
    (delta: number) => {
      const next = SPLITS[(activeIndex + delta + SPLITS.length) % SPLITS.length];
      if (next) setCategory(next.category);
    },
    [activeIndex, setCategory],
  );

  useEffect(() => {
    if (!enabled) return;
    if (paletteOpen || settingsOpen || appearanceOpen || composerOpen) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;

      // `[` and `]` always work; Tab only when it is not needed for focus.
      if (e.key === ']' || e.key === '[') {
        const target = e.target as HTMLElement | null;
        if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) {
          return;
        }
        e.preventDefault();
        move(e.key === ']' ? 1 : -1);
        return;
      }

      if (e.key === 'Tab' && tabIsFree()) {
        e.preventDefault();
        move(e.shiftKey ? -1 : 1);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, paletteOpen, settingsOpen, appearanceOpen, composerOpen, move]);

  // A split scrolled out of view after a Tab is a split the user cannot find.
  useEffect(() => {
    if (!enabled) return;
    stripRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [enabled, activeIndex]);

  // Sent and Trash have no splits, but the row still has to say where you are:
  // an empty top row over a list of strangers' names is disorienting.
  if (!enabled) {
    return (
      <div className={styles.strip}>
        <span className={styles.standalone}>{VIEW_LABEL[view] ?? view}</span>
      </div>
    );
  }

  return (
    <div
      className={styles.strip}
      ref={stripRef}
      role="tablist"
      aria-label="Inbox splits"
      aria-orientation="horizontal"
    >
      {SPLITS.map((split, i) => {
        const active = i === activeIndex;
        const count = counts.get(split.key) ?? 0;
        return (
          <button
            key={split.key}
            type="button"
            role="tab"
            className={styles.tab}
            data-active={active}
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            aria-controls="kiln-thread-list"
            title={`${split.label} · ] and [ move between splits (⇥ too, in the list)`}
            onClick={() => setCategory(split.category)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') {
                e.preventDefault();
                move(1);
              } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                move(-1);
              }
            }}
          >
            <span className={styles.label}>{split.label}</span>
            {/* Hidden at zero and above 999 — Superhuman's exact rule. */}
            {count > 0 && count <= 999 && (
              <span className={styles.count} data-numeric>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default SplitTabs;
